import React from "react";
import {
  Dialog,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import CustomIconButton from "../../Components/CustomIconButton";
import CustomContainedButton from "../../Components/CustomContainedButton";
import { getNextPath, NodeType } from "./Utils";
import { convertFirstUpper } from "../../Regex";

interface PasteConfirmDialogProps {
  open: boolean;
  copiedData: any[];
  onClose: () => void;
  onConfirm: (level: NodeType, move_ids: number[]) => void;
  onDelete: (index: number) => void;
  onChildDelete: (parentIdx: number, idToRemove: any) => void; // 👈 new prop
  type: NodeType;
  destination: string;
}

const PasteConfirmDialog: React.FC<PasteConfirmDialogProps> = ({
  open,
  copiedData,
  onClose,
  onConfirm,
  onDelete,
  onChildDelete,
  type,
  destination,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* 🔹 Header Section */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "#f8fafc",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Confirm Paste Operation
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          You’re about to move{" "}
          <strong>
            {`${
              copiedData.length === 1
                ? copiedData[0].childs?.length
                : copiedData.length
            } ${convertFirstUpper(getNextPath(type).toLowerCase())}${
              copiedData.length > 1
                ? "s"
                : copiedData[0].childs?.length > 1
                ? "s"
                : ""
            }${
              copiedData.length === 1
                ? ` from ${copiedData[0].parent.label}`
                : ""
            }`}
          </strong>{" "}
          to{" "}
          <Chip
            label={`${convertFirstUpper(type.toLowerCase())}: ${destination}`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, ml: 0.5 }}
          />
        </Typography>
      </Box>

      {/* 🔹 Content Section */}
      <Box sx={{ p: 3 }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9fafc" }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>IDs</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {copiedData.map((item, idx) => {
                const isParent = item.parent;
                const ids = isParent
                  ? item.childs?.map((q: any) => ({ id: q.id, label: q.text }))
                  : item.questions?.map((q: any) => ({
                      id: q.id,
                      label: q.id,
                    }));

                return (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>

                    {/* 🔹 Source */}
                    <TableCell>
                      {isParent ? (
                        <Chip
                          label={`${convertFirstUpper(
                            item.parent.type.toLowerCase()
                          )}: ${item.parent.label}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" noWrap>
                          {item.series?.label} → {item.mocktest?.label} →{" "}
                          {item.section?.label}
                        </Typography>
                      )}
                    </TableCell>

                    {/* 🔹 Details */}
                    <TableCell>
                      <Typography variant="body2">
                        {isParent
                          ? `${item.childs.length} ${convertFirstUpper(
                              getNextPath(item.parent.type).toLowerCase()
                            )}${item.childs.length > 1 ? "s" : ""}`
                          : `${item.questions.length} Questions`}
                      </Typography>
                    </TableCell>

                    {/* 🔹 IDs */}
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {ids?.map(({ id, label }: any) => (
                          <Tooltip key={id} title="Remove this item">
                            <Chip
                              label={label}
                              size="small"
                              variant="outlined"
                              onDelete={() => onChildDelete(idx, id)} // 👈 new action
                              deleteIcon={
                                <Delete
                                  sx={{
                                    fontSize: "0.9rem",
                                  }}
                                />
                              }
                              sx={{
                                cursor: "pointer",
                                fontSize: "0.75rem",
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 🔹 Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <CustomIconButton content="Cancel" onClick={onClose} />
          <CustomContainedButton
            content="Yes, Paste"
            otherProps={{
              onClick: () => {
                onConfirm(
                  getNextPath(type),
                  copiedData[0].childs.map((s: { id: any }) => s.id)
                );
              },
              disabled: copiedData.length === 0,
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default PasteConfirmDialog;
